import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { obtenerPagoPorId, enviarFacturaPorEmail, me } from "../api";
import logo from "../assets/LOGO - FOOTER.png";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./FacturaView.css";

export default function FacturaView() {
  const { id } = useParams();
  const [factura, setFactura] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [user, setUser] = useState(null);
  const facturaRef = useRef();

  useEffect(() => {
    me().then((r) => {
      if (r.user) setUser(r.user);
    });
  }, []);

  useEffect(() => {
    const loadFactura = async () => {
      try {
        const r = await obtenerPagoPorId(id);
        setFactura(r.item);
      } catch (err) {
        console.error("Error al cargar la factura", err);
      }
    };
    loadFactura();
  }, [id]);

  if (!factura) return <p style={{ textAlign: "center" }}>Cargando factura...</p>;

  // -------------------------
  // IVA / Exención (ROBUSTO)
  // -------------------------
  const baseAmount = parseFloat(factura.amount) || 0;

  const serviceText = String(factura.service_name || "").toLowerCase().trim();
  const specialtyCode = String(factura.specialty_code || "").toLowerCase().trim();
  const specialtyName = String(factura.specialty_name || "").toLowerCase().trim();

  // Yoga si lo detectamos por service_name, specialty_code o specialty_name
  const isYoga =
    serviceText.includes("yoga") ||
    specialtyCode.includes("yoga") ||
    specialtyName.includes("yoga");

  // Sanitarias exentas: psicología, logopedia, nutrición
  // (y también "psicoterapia" porque tus servicios se llaman así)
  const isSanitarioExento =
    serviceText.includes("psicoterapia") ||
    serviceText.includes("psicolog") ||
    serviceText.includes("logoped") ||
    serviceText.includes("nutric") ||
    specialtyCode.includes("psicolog") ||
    specialtyCode.includes("logop") ||
    specialtyCode.includes("nutric") ||
    specialtyName.includes("psicolog") ||
    specialtyName.includes("logoped") ||
    specialtyName.includes("nutric");

  // Exención SOLO si es sanitario y NO yoga
  const isExentoIVA = !isYoga && isSanitarioExento;

  // IVA solo para yoga
  const iva = isYoga ? baseAmount * 0.21 : 0;
  const total = baseAmount + iva;

  const exencionIVA =
    "Operación exenta de IVA según el artículo 20.Uno.3 de la Ley 37/1992 del IVA.";

  // -------------------------
  // Servicios de tipo mes
  // -------------------------
  const serviciosMes = new Set([
    "intervencion_psicoterapia_mes",
    "intervencion_estimulacion_mes",
    "intervencion_reeducacion_NEAE_mes",
    "intervencion_logopedia_mes",
    "intervencion_logopedia_NEAE_mes",
  ]);

  const getFechaMesFacturado = () => {
    const posibleFechaSesion = factura.session_start || factura.date;
    const d = new Date(posibleFechaSesion);
    if (Number.isNaN(d.getTime())) return new Date(factura.date);
    return d;
  };

  const fechaMesFacturado = getFechaMesFacturado();

  const getConceptoConMes = () => {
    const rawCode = factura.service_name || "";
    let concepto = rawCode.replace(/_/g, " ");

    if (serviciosMes.has(rawCode)) {
      const mesTexto = fechaMesFacturado.toLocaleDateString("es-ES", {
        month: "long",
        year: "numeric",
      });

      if (/mes/i.test(concepto)) {
        concepto = concepto.replace(/mes/i, `mes ${mesTexto}`);
      } else {
        concepto = `${concepto} (${mesTexto})`;
      }
    }
    return concepto;
  };

  const conceptoServicio = getConceptoConMes();

  const mesFacturadoTexto = fechaMesFacturado.toLocaleDateString("es-ES", {
    month: "long",
    year: "numeric",
  });

  // -------------------------
  // PDF
  // -------------------------
  const handleDownloadPDF = async () => {
    const input = facturaRef.current;
    const canvas = await html2canvas(input, { scale: 2 });
    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pageWidth, imgHeight);
    pdf.save(`factura_${factura.id}.pdf`);
  };

  // EMAIL (solo admin)
  const handleSendEmail = async () => {
    setEnviando(true);
    setMensaje("");

    try {
      const r = await enviarFacturaPorEmail(factura.id);
      setMensaje(`✅ Correo enviado correctamente a ${r.email}`);
    } catch (err) {
      setMensaje("❌ Error al enviar el correo.");
      console.error(err);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="factura-page">
      <div className="factura-actions">
        <button onClick={handleDownloadPDF} className="btn-pdf">
          📄 Descargar PDF
        </button>

        {user?.role === "admin" && (
          <button onClick={handleSendEmail} className="btn-email" disabled={enviando}>
            {enviando ? "📤 Enviando..." : "✉️ Enviar correo"}
          </button>
        )}
      </div>

      {mensaje && (
        <p
          style={{
            textAlign: "center",
            color: mensaje.startsWith("✅") ? "green" : "red",
          }}
        >
          {mensaje}
        </p>
      )}

      <div className="factura-container" ref={facturaRef}>
        <header className="factura-header">
          <img src={logo} alt="Te Ayudo" className="factura-logo" />
          <div className="factura-info">
            <p>Avenida Alcalde Jesús Mantaras, local 5</p>
            <p>Jerez de la Frontera, Cádiz 11405</p>
            <p>Tel: +34 601 866 199</p>
          </div>
        </header>

        <h2 className="factura-title">Factura</h2>

        <div className="factura-details">
          <p>
            <strong>Factura Nº:</strong> {factura.id}
          </p>
          <p>
            <strong>Fecha pago:</strong>{" "}
            {new Date(factura.date).toLocaleDateString("es-ES")}
          </p>
          <p>
            <strong>Mes facturado:</strong> {mesFacturadoTexto}
          </p>
          <p>
            <strong>Paciente:</strong> {factura.paciente}
          </p>
          <p>
            <strong>Servicio:</strong> {conceptoServicio}
          </p>

          {factura.profesional_nombre && (
            <>
              <hr style={{ margin: "10px 0", borderTop: "1px solid #ddd" }} />
              <p>
                <strong>Profesional:</strong> {factura.profesional_nombre}{" "}
                {factura.profesional_apellidos || ""}
              </p>
              <p>
                <strong>NIF:</strong> {factura.profesional_nif || "—"}
              </p>
            </>
          )}
        </div>

        <table className="factura-table">
          <thead>
            <tr>
              <th>Concepto</th>
              <th style={{ textAlign: "right" }}>Importe (€)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{conceptoServicio}</td>
              <td style={{ textAlign: "right" }}>{baseAmount.toFixed(2)}</td>
            </tr>

            {isYoga && (
              <tr>
                <td style={{ textAlign: "right", fontWeight: "bold" }}>
                  IVA (21%)
                </td>
                <td style={{ textAlign: "right" }}>{iva.toFixed(2)} €</td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="factura-total">Total: {total.toFixed(2)} €</div>

        {isExentoIVA && (
          <div style={{ marginTop: 10, fontSize: 12, fontStyle: "italic", color: "#555" }}>
            {exencionIVA}
          </div>
        )}

        <footer className="factura-footer">
          <p>Gracias por su confianza en TE AYUDO</p>
        </footer>
      </div>
    </div>
  );
}
